import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import MarketingLinks from '../components/MarketingLinks';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { API_BASE_URL } from '../config/api';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const hasHtmlContent = (html) => {
  if (!html || !String(html).trim()) return false;
  const text = String(html).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length > 0;
};

function MailingListSubscribersPage() {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('confirmed');
  const [tagFilter, setTagFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subscriberToDelete, setSubscriberToDelete] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: '', email: '', tags: [] });
  const [addTagInput, setAddTagInput] = useState('');
  const [addSaving, setAddSaving] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', email: '', status: 'confirmed', tags: [] });
  const [tagInput, setTagInput] = useState('');

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesSubscriber, setNotesSubscriber] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [editingNoteDateId, setEditingNoteDateId] = useState(null);
  const [editingNoteDateValue, setEditingNoteDateValue] = useState('');

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubscriber, setEmailSubscriber] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [showEmailLogModal, setShowEmailLogModal] = useState(false);
  const [emailLogSubscriber, setEmailLogSubscriber] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [viewingEmail, setViewingEmail] = useState(null);

  useEffect(() => {
    document.title = 'Mailing List Subscribers - Design Store Admin';
    fetchSubscribers();
    fetchTemplates();
  }, []);

  useEffect(() => {
    let filtered = [...subscribers];
    if (statusFilter === 'confirmed') {
      filtered = filtered.filter(s => s.confirmed && !s.unsubscribed);
    } else if (statusFilter === 'unconfirmed') {
      filtered = filtered.filter(s => !s.confirmed && !s.unsubscribed);
    } else if (statusFilter === 'unsubscribed') {
      filtered = filtered.filter(s => s.unsubscribed);
    }
    if (tagFilter) {
      filtered = filtered.filter(s => (s.tags || []).includes(tagFilter));
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(s =>
        (s.firstName || '').toLowerCase().includes(term) ||
        (s.email || '').toLowerCase().includes(term)
      );
    }
    setFilteredSubscribers(filtered);
  }, [subscribers, statusFilter, tagFilter, searchTerm]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authAxios.get(`${API_BASE_URL}/mailing-list/admin/subscribers`);
      if (response.data.success) {
        setSubscribers(response.data.data);
      } else {
        setError('Failed to fetch subscribers');
      }
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(err.response?.data?.error || 'Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await authAxios.get(`${API_BASE_URL}/admin/outreach/templates`);
      if (response.data.success) {
        setTemplates(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const allTags = [...new Set(subscribers.flatMap(s => s.tags || []))].filter(Boolean).sort();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toDatetimeLocal = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const getStatusBadge = (subscriber) => {
    if (subscriber.unsubscribed) {
      return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Unsubscribed</span>;
    } else if (subscriber.confirmed) {
      return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Confirmed</span>;
    } else {
      return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Unconfirmed</span>;
    }
  };

  const canSendEmail = (s) => s.confirmed && !s.unsubscribed && s.email && emailRegex.test(s.email.trim());

  const getStatusFromSubscriber = (s) => {
    if (s?.unsubscribed) return 'unsubscribed';
    if (s?.confirmed) return 'confirmed';
    return 'unconfirmed';
  };

  const handleEditSubscriber = (subscriber) => {
    setEditingSubscriber(subscriber);
    setEditForm({
      firstName: subscriber.firstName || '',
      email: subscriber.email || '',
      status: getStatusFromSubscriber(subscriber),
      tags: subscriber.tags || []
    });
    setTagInput('');
    setShowEditModal(true);
  };

  const handleSaveSubscriber = async () => {
    if (!editingSubscriber || !editForm.firstName.trim()) {
      setToast({ message: 'First name is required', type: 'error' });
      return;
    }
    if (!editForm.email.trim() || !emailRegex.test(editForm.email.trim())) {
      setToast({ message: 'Valid email is required', type: 'error' });
      return;
    }
    const tagsToSave = [...editForm.tags];
    if (tagInput.trim()) {
      const t = tagInput.trim();
      if (!tagsToSave.includes(t)) tagsToSave.push(t);
    }
    try {
      const response = await authAxios.put(
        `${API_BASE_URL}/mailing-list/admin/subscribers/${editingSubscriber.id}`,
        { firstName: editForm.firstName.trim(), email: editForm.email.trim(), status: editForm.status, tags: tagsToSave }
      );
      if (response.data.success) {
        setSubscribers(subscribers.map(s => s.id === editingSubscriber.id ? { ...s, ...response.data.data } : s));
        setToast({ message: 'Subscriber updated', type: 'success' });
        setShowEditModal(false);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update subscriber', type: 'error' });
    }
  };

  const handleOpenNotes = (subscriber) => {
    setNotesSubscriber(subscriber);
    setNewNote('');
    setEditingNoteDateId(null);
    setEditingNoteDateValue('');
    setShowNotesModal(true);
  };

  const handleAddNote = async () => {
    if (!notesSubscriber || !newNote.trim()) return;
    try {
      const response = await authAxios.post(
        `${API_BASE_URL}/mailing-list/admin/subscribers/${notesSubscriber.id}/notes`,
        { note: newNote }
      );
      if (response.data.success) {
        setSubscribers(subscribers.map(s => s.id === notesSubscriber.id ? response.data.data : s));
        setNotesSubscriber(response.data.data);
        setNewNote('');
        setToast({ message: 'Note added', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to add note', type: 'error' });
    }
  };

  const handleStartEditNoteDate = (n) => {
    setEditingNoteDateId(n.id || n.date);
    setEditingNoteDateValue(toDatetimeLocal(n.date));
  };

  const handleSaveNoteDate = async () => {
    if (!notesSubscriber || !editingNoteDateId || !editingNoteDateValue) {
      setEditingNoteDateId(null);
      setEditingNoteDateValue('');
      return;
    }
    try {
      const response = await authAxios.put(
        `${API_BASE_URL}/mailing-list/admin/subscribers/${notesSubscriber.id}/notes/${editingNoteDateId}`,
        { date: new Date(editingNoteDateValue).toISOString() }
      );
      if (response.data.success) {
        setSubscribers(subscribers.map(s => s.id === notesSubscriber.id ? response.data.data : s));
        setNotesSubscriber(response.data.data);
        setToast({ message: 'Note date updated', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to update date', type: 'error' });
    }
    setEditingNoteDateId(null);
    setEditingNoteDateValue('');
  };

  const handleNoteDateBlur = () => {
    if (editingNoteDateId) handleSaveNoteDate();
  };

  const handleNoteDateKeyDown = (e) => {
    if (e.key === 'Enter') handleSaveNoteDate();
    if (e.key === 'Escape') {
      setEditingNoteDateId(null);
      setEditingNoteDateValue('');
    }
  };

  const handleOpenSendEmail = (subscriber) => {
    setEmailSubscriber(subscriber);
    setEmailSubject('');
    setEmailMessage('');
    setSelectedTemplateId('');
    setShowEmailModal(true);
  };

  const handleSelectTemplate = (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    const t = templates.find(x => x.id === id);
    if (t) {
      setEmailSubject(t.subject || '');
      setEmailMessage(t.message || '');
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubscriber || !emailSubject.trim() || !emailMessage.trim()) {
      setToast({ message: 'Subject and message are required', type: 'error' });
      return;
    }
    if (!hasHtmlContent(emailMessage)) {
      setToast({ message: 'Message is required', type: 'error' });
      return;
    }
    setEmailSending(true);
    try {
      const response = await authAxios.post(
        `${API_BASE_URL}/mailing-list/admin/subscribers/${emailSubscriber.id}/send-email`,
        { subject: emailSubject.trim(), message: emailMessage.trim() }
      );
      if (response.data.success) {
        setToast({ message: 'Email sent successfully', type: 'success' });
        setShowEmailModal(false);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to send email', type: 'error' });
    } finally {
      setEmailSending(false);
    }
  };

  const handleOpenEmailLog = async (subscriber) => {
    setEmailLogSubscriber(subscriber);
    setShowEmailLogModal(true);
    setEmailLogLoading(true);
    try {
      const response = await authAxios.get(`${API_BASE_URL}/mailing-list/admin/subscribers/${subscriber.id}/emails`);
      if (response.data.success) {
        setEmailLog(response.data.data);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to load email log', type: 'error' });
    } finally {
      setEmailLogLoading(false);
    }
  };

  const handleDeleteClick = (subscriber) => {
    setSubscriberToDelete(subscriber);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subscriberToDelete) return;
    try {
      const response = await authAxios.delete(`${API_BASE_URL}/mailing-list/admin/subscribers/${subscriberToDelete.id}`);
      if (response.data.success) {
        setSubscribers(subscribers.filter(s => s.id !== subscriberToDelete.id));
        setToast({ message: 'Subscriber deleted successfully', type: 'success' });
        setShowDeleteConfirm(false);
        setSubscriberToDelete(null);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to delete subscriber', type: 'error' });
    }
  };

  const addTagToEdit = (tag) => {
    const t = String(tag).trim();
    if (t && !editForm.tags.includes(t)) {
      setEditForm(f => ({ ...f, tags: [...f.tags, t] }));
      setTagInput('');
    }
  };

  const removeTagFromEdit = (tag) => {
    setEditForm(f => ({ ...f, tags: f.tags.filter(x => x !== tag) }));
  };

  const addTagToAdd = (tag) => {
    const t = String(tag).trim();
    if (t && !addForm.tags.includes(t)) {
      setAddForm(f => ({ ...f, tags: [...f.tags, t] }));
      setAddTagInput('');
    }
  };

  const removeTagFromAdd = (tag) => {
    setAddForm(f => ({ ...f, tags: f.tags.filter(x => x !== tag) }));
  };

  const handleOpenAddModal = () => {
    setAddForm({ firstName: '', email: '', tags: [] });
    setAddTagInput('');
    setShowAddModal(true);
  };

  const handleAddSubscriber = async () => {
    if (!addForm.firstName.trim()) {
      setToast({ message: 'First name is required', type: 'error' });
      return;
    }
    if (!addForm.email.trim() || !emailRegex.test(addForm.email.trim())) {
      setToast({ message: 'Valid email is required', type: 'error' });
      return;
    }
    const tagsToSave = [...addForm.tags];
    if (addTagInput.trim()) {
      const t = addTagInput.trim();
      if (!tagsToSave.includes(t)) tagsToSave.push(t);
    }
    setAddSaving(true);
    try {
      const response = await authAxios.post(
        `${API_BASE_URL}/mailing-list/admin/subscribers`,
        { firstName: addForm.firstName.trim(), email: addForm.email.trim(), tags: tagsToSave }
      );
      if (response.data.success) {
        setSubscribers([response.data.data, ...subscribers]);
        setToast({ message: 'Subscriber added', type: 'success' });
        setShowAddModal(false);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to add subscriber', type: 'error' });
    } finally {
      setAddSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <AdminTabs />
          <MarketingLinks />
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading subscribers...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-8 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <AdminHeader />
          <AdminTabs />
          <MarketingLinks />
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button onClick={fetchSubscribers} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />
        <AdminTabs />
        <MarketingLinks />

        <div className="mb-6 flex flex-wrap items-end gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Mailing List Subscribers</h2>
            <p className="text-sm text-gray-600">Manage subscribers, send emails, add notes, and segment by tags.</p>
          </div>
          <div className="flex-1 flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Search name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="confirmed">Confirmed</option>
              <option value="unconfirmed">Unconfirmed</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="all">All</option>
            </select>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All Tags</option>
              {allTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            Add Subscriber
          </button>
          <button
            onClick={() => navigate('/admin', { state: { activeTab: 'outreach-email' } })}
            className="px-4 py-2 text-blue-600 hover:text-blue-800 text-sm"
          >
            Outreach Email Settings
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          Showing {filteredSubscribers.length} of {subscribers.length} subscribers
        </div>

        {filteredSubscribers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600">No subscribers found.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscribed</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{subscriber.firstName}</td>
                      <td className="px-6 py-4 text-sm">
                        <a href={`mailto:${subscriber.email}`} className="text-blue-600 hover:underline">{subscriber.email}</a>
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(subscriber)}</td>
                      <td className="px-6 py-4 text-sm">
                        {(subscriber.tags || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {(subscriber.tags || []).map(t => (
                              <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{t}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{formatDate(subscriber.createdAt)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-0">
                          <button onClick={() => handleEditSubscriber(subscriber)} title="Edit" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => handleOpenNotes(subscriber)} title="Notes" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          </button>
                          {canSendEmail(subscriber) ? (
                            <button onClick={() => handleOpenSendEmail(subscriber)} title="Send Email" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            </button>
                          ) : (
                            <span title="Cannot send (unconfirmed or unsubscribed)" className="p-1 text-gray-300 cursor-not-allowed inline-flex">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                            </span>
                          )}
                          <button onClick={() => handleOpenEmailLog(subscriber)} title="Email Log" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                          </button>
                          <button onClick={() => handleDeleteClick(subscriber)} title="Delete" className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Subscriber Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Add Subscriber</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name *</label>
                  <input
                    type="text"
                    value={addForm.firstName}
                    onChange={(e) => setAddForm(f => ({ ...f, firstName: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm(f => ({ ...f, email: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tags</label>
                  <div className="mt-1 flex flex-wrap gap-2 mb-2">
                    {addForm.tags.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {t}
                        <button type="button" onClick={() => removeTagFromAdd(t)} className="hover:text-blue-900">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add tag..."
                      value={addTagInput}
                      onChange={(e) => setAddTagInput(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addTagToAdd(addTagInput); }
                      }}
                    />
                    <button type="button" onClick={() => addTagToAdd(addTagInput)} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">Add</button>
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) { addTagToAdd(v); e.target.value = ''; }
                      }}
                    >
                      <option value="">— Existing tags —</option>
                      {allTags.filter(t => !addForm.tags.includes(t)).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setShowAddModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                <button onClick={handleAddSubscriber} disabled={addSaving || !addForm.firstName.trim() || !addForm.email.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {addSaving ? 'Adding...' : 'Add Subscriber'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Subscriber Modal */}
        {showEditModal && editingSubscriber && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold">Edit Subscriber</h3>
                <div className="flex items-center gap-0">
                  <button onClick={() => handleOpenNotes(editingSubscriber)} title="Notes" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </button>
                  {canSendEmail(editingSubscriber) && (
                    <button onClick={() => { setShowEditModal(false); handleOpenSendEmail(editingSubscriber); }} title="Send Email" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </button>
                  )}
                  <button onClick={() => handleOpenEmailLog(editingSubscriber)} title="Email Log" className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name *</label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm(f => ({ ...f, firstName: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="confirmed">Confirmed</option>
                    <option value="unconfirmed">Unconfirmed</option>
                    <option value="unsubscribed">Unsubscribed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tags</label>
                  <div className="mt-1 flex flex-wrap gap-2 mb-2">
                    {editForm.tags.map(t => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                        {t}
                        <button type="button" onClick={() => removeTagFromEdit(t)} className="hover:text-blue-900">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add tag..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTagToEdit(tagInput);
                        }
                      }}
                    />
                    <button type="button" onClick={() => addTagToEdit(tagInput)} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm">Add</button>
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) { addTagToEdit(v); e.target.value = ''; }
                      }}
                    >
                      <option value="">— Existing tags —</option>
                      {allTags.filter(t => !editForm.tags.includes(t)).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                <button onClick={handleSaveSubscriber} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save</button>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {showNotesModal && notesSubscriber && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Notes for {notesSubscriber.firstName}</h3>
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {(notesSubscriber.notes || []).map((n) => (
                  <div key={n.id || n.date} className="p-2 bg-gray-50 rounded text-sm">
                    {editingNoteDateId === (n.id || n.date) ? (
                      <input
                        type="datetime-local"
                        value={editingNoteDateValue}
                        onChange={(e) => setEditingNoteDateValue(e.target.value)}
                        onBlur={handleNoteDateBlur}
                        onKeyDown={handleNoteDateKeyDown}
                        className="text-gray-500 text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                      />
                    ) : (
                      <span
                        className="text-gray-500 cursor-pointer hover:text-gray-700 hover:underline"
                        onClick={() => handleStartEditNoteDate(n)}
                        title="Click to change date/time"
                      >
                        {formatDate(n.date)}
                      </span>
                    )}
                    <p className="mt-1">{n.note}</p>
                  </div>
                ))}
                {(!notesSubscriber.notes || notesSubscriber.notes.length === 0) && (
                  <p className="text-gray-500 text-sm">No notes yet.</p>
                )}
              </div>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowNotesModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
                <button onClick={handleAddNote} disabled={!newNote.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Add Note</button>
              </div>
            </div>
          </div>
        )}

        {/* Send Email Modal */}
        {showEmailModal && emailSubscriber && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Send Email to {emailSubscriber.firstName}</h3>
              <p className="text-sm text-gray-500 mb-4">To: {emailSubscriber.email}</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={handleSelectTemplate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">— Select template —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Email subject"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={emailMessage}
                    onChange={setEmailMessage}
                    modules={QUILL_MODULES}
                    placeholder="Compose your message..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowEmailModal(false)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
                <button onClick={handleSendEmail} disabled={emailSending || !emailSubject.trim() || !hasHtmlContent(emailMessage)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {emailSending ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Log Modal */}
        {showEmailLogModal && emailLogSubscriber && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-2">Email Log for {emailLogSubscriber.firstName}</h3>
              {emailLogLoading ? (
                <p className="text-gray-500">Loading...</p>
              ) : emailLog.length === 0 ? (
                <p className="text-gray-500">No emails sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {emailLog.map((e) => (
                    <div
                      key={e.id}
                      className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                      onClick={() => setViewingEmail(e)}
                    >
                      <div className="font-medium text-sm">{e.subject}</div>
                      <div className="text-gray-500 text-sm">{formatDate(e.sentAt)}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <button onClick={() => { setShowEmailLogModal(false); setViewingEmail(null); }} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* View Email Modal */}
        {viewingEmail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setViewingEmail(null)}>
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-semibold mb-2">Email Sent {formatDate(viewingEmail.sentAt)}</h3>
              <p className="text-sm text-gray-500 mb-2">To: {viewingEmail.subscriberEmail}</p>
              <p className="text-sm font-medium mb-1">{viewingEmail.subject}</p>
              <div className="border border-gray-200 rounded p-4 bg-gray-50 text-sm prose max-w-none" dangerouslySetInnerHTML={{ __html: viewingEmail.message || '' }} />
              <button onClick={() => setViewingEmail(null)} className="mt-4 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
            </div>
          </div>
        )}

        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          title="Delete Subscriber"
          message={`Are you sure you want to delete ${subscriberToDelete?.firstName} (${subscriberToDelete?.email}) from the mailing list? This action cannot be undone.`}
          onConfirm={handleDeleteConfirm}
          onCancel={() => { setShowDeleteConfirm(false); setSubscriberToDelete(null); }}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}

export default MailingListSubscribersPage;
